import { Link } from "wouter";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/layout/Footer";
import { LoginModal } from "@/components/auth/LoginModal";
import { SEO } from "@/components/layout/SEO";
import { ContactUs } from "@/components/layout/ContactUs";
import { useState, useEffect, lazy, Suspense } from "react";
import { isAuthenticated } from "@/lib/supabase";
import { useLocation } from "wouter";
import { homepageDemos } from "@/lib/data/demos";
// import { DemoSection } from "@/components/sections/DemoSection";

// Landing Page Components
import { HeroSection } from "@/components/landing/HeroSection";
import { FeaturesBar } from "@/components/landing/FeaturesBar";
import { QuickActions } from "@/components/landing/QuickActions";

// Static imports for below-the-fold components to prevent hydration mismatch
// Converted to lazy imports to reduce initial bundle size
const HowItWorksSection = lazy(() => import("@/components/landing/HowItWorksSection").then(module => ({ default: module.HowItWorksSection })));
const UseCases = lazy(() => import("@/components/landing/UseCases").then(module => ({ default: module.UseCases })));
const StatsSection = lazy(() => import("@/components/landing/StatsSection").then(module => ({ default: module.StatsSection })));
const Testimonials = lazy(() => import("@/components/landing/Testimonials").then(module => ({ default: module.Testimonials })));
const TrustSection = lazy(() => import("@/components/landing/TrustSection").then(module => ({ default: module.TrustSection })));
const FAQSection = lazy(() => import("@/components/landing/FAQSection").then(module => ({ default: module.FAQSection })));
const ComparisonTable = lazy(() => import("@/components/landing/ComparisonTable").then(module => ({ default: module.ComparisonTable })));
const FinalCTA = lazy(() => import("@/components/landing/FinalCTA").then(module => ({ default: module.FinalCTA })));
const BlogPreviewSection = lazy(() => import("@/components/landing/BlogPreviewSection").then(module => ({ default: module.BlogPreviewSection })));
const DemoSection = lazy(() => import("@/components/sections/DemoSection").then(module => ({ default: module.DemoSection })));

// Skeleton loader for sections
const SectionSkeleton = () => (
  <div className="py-20 container mx-auto px-4">
    <div className="h-12 w-48 bg-muted/20 rounded-lg mx-auto mb-8 animate-pulse" />
    <div className="h-64 w-full bg-muted/10 rounded-xl animate-pulse" />
  </div>
);

export default function Landing() {
  const [, setLocation] = useLocation();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isAuth, setIsAuth] = useState(false);
  const [showStickyButton, setShowStickyButton] = useState(false);

  // Check authentication status
  useEffect(() => {
    isAuthenticated().then(setIsAuth);
  }, []);

  // Sticky CTA button on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyButton(window.scrollY > 600);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle CTA click
  const handleGetStarted = () => {
    if (isAuth) {
      setLocation("/dashboard");
    } else {
      setShowLoginModal(true);
    }
  };

  // SEO structured data for homepage
  const siteUrl = "https://photoshoprequest.app";
  const organizationId = `${siteUrl}/#organization`;
  const productId = `${siteUrl}/#photo-editing-service`;

  const websiteStructuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      "name": "PhotoshopRequest.app",
      "alternateName": "Photoshop Request",
      "url": siteUrl,
      "description": "AI-powered photo editing in 30 seconds. Professional photo editing service with instant AI edits or human editors for complex projects.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${siteUrl}/create?q={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": organizationId,
      "name": "PhotoshopRequest.app",
      "url": siteUrl,
      "logo": `${siteUrl}/logo.webp`,
      "description": "AI-powered photo editing service offering 30+ professional editing services including person removal, background removal, image enhancement, color correction, and more.",
      "foundingDate": "2024",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Customer Support",
        "availableLanguage": "English"
      },
      "sameAs": [],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "1000000",
        "bestRating": "5",
        "worstRating": "1"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Photo Editing Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Remove Person from Photo AI",
              "description": "Delete unwanted people from any photo - our #1 most requested edit"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Image Enhancement & Lighting Fix",
              "description": "Fix dark photos, blur, grain, and poor lighting instantly"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Remove Background AI",
              "description": "Transparent backgrounds or replace with any scene"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Color Correction & Photo Recolor",
              "description": "Change clothing colors, objects, or fix color balance"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Pet & Animal Photo Editor",
              "description": "Remove pets, recolor fur, or perfect your animal photos"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Add Text & Logo Watermark",
              "description": "Add copyright text, logos, or watermarks professionally"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Upscale Image to 4K AI",
              "description": "AI super-resolution for crystal clear, high-res photos"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Photo Restoration Service",
              "description": "Restore damaged, old, or faded family photos"
            }
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "@id": productId,
      "name": "PhotoshopRequest.app Photo Editing Service",
      "description": "AI-powered photo editing service. Get professional edits in 30 seconds or work with human editors for complex projects.",
      "url": siteUrl,
      "image": `${siteUrl}/seo/og-image.webp`,
      "category": "Photo editing service",
      "brand": {
        "@type": "Brand",
        "name": "PhotoshopRequest.app",
        "url": siteUrl,
        "logo": `${siteUrl}/logo.webp`
      },
      "offers": {
        "@type": "Offer",
        "price": "2.99",
        "priceCurrency": "USD",
        "description": "AI photo editing service",
        "url": `${siteUrl}/pricing`,
        "availability": "https://schema.org/InStock",
        "hasMerchantReturnPolicy": {
          "@type": "MerchantReturnPolicy",
          "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
          "merchantReturnDays": 7,
          "returnMethod": "https://schema.org/ReturnByMail",
          "returnFees": "https://schema.org/FreeReturn"
        },
        "shippingDetails": {
          "@type": "OfferShippingDetails",
          "shippingDestination": {
            "@type": "DefinedRegion",
            "addressCountry": "US"
          },
          "shippingRate": {
            "@type": "MonetaryAmount",
            "value": "0",
            "currency": "USD"
          },
          "deliveryTime": {
            "@type": "ShippingDeliveryTime",
            "handlingTime": {
              "@type": "QuantitativeValue",
              "minValue": 0,
              "maxValue": 0,
              "unitCode": "d"
            },
            "transitTime": {
              "@type": "QuantitativeValue",
              "minValue": 0,
              "maxValue": 0,
              "unitCode": "d"
            }
          }
        }
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "547",
        "bestRating": "5",
        "worstRating": "1"
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-primary/5 overflow-x-hidden">
      <SEO
        title="AI Photo Editor Online Free - PhotoshopRequest.app | Edit Photos in 30 Seconds"
        description="Professional AI photo editing in 30 seconds. Remove people from photos, remove backgrounds, enhance images, upscale to 4K, color correction, pet photos, add watermarks, restore old photos. $2.99 per edit. Start free!"
        keywords="remove person from photo, AI photo editor, remove background, image enhancement, color correction photo, upscale image AI, pet photo editor, add text to photo, photo restoration, online photo editing, photoshop alternative, free photo editor, professional photo editing service, no subscription photo editor, pay per download photo editor, human photo editor backup, conversational photo editor, edit photos with text, AI agent for photos, remove ex from photo, merge photos AI, batch photo editor"
        canonicalUrl="https://photoshoprequest.app/"
        ogImage="https://photoshoprequest.app/seo/seo/og-image.webp"
        structuredData={websiteStructuredData}
      />

      <HeroSection />
      <FeaturesBar />
      <QuickActions />

      <Suspense fallback={<SectionSkeleton />}>
        <HowItWorksSection />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <UseCases />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <StatsSection />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <DemoSection examples={homepageDemos} />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <Testimonials />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <TrustSection />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <FAQSection />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <ComparisonTable />
      </Suspense>
      <ContactUs backgroundColor="bg-background" />
      <Suspense fallback={<SectionSkeleton />}>
        <BlogPreviewSection />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <FinalCTA />
      </Suspense>
      <Footer />

      {/* Sticky Floating CTA Button */}
      <div
        className={`fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-auto z-50 transition-all duration-500 ${showStickyButton ? 'translate-y-0 opacity-100' : 'translate-y-32 opacity-0 pointer-events-none'
          }`}
      >
        <Link href="/create">
          <Button
            size="lg"
            className="w-full sm:w-auto gap-2 h-14 px-6 sm:px-8 shadow-2xl bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-300 group animate-none sm:animate-bounce"
          >
            <Sparkles className="h-5 w-5 group-hover:rotate-12 transition-transform" />
            Start Editing
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </Link>
      </div>

      {/* Login Modal */}
      <LoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={() => {
          setShowLoginModal(false);
          setLocation("/dashboard");
        }}
      />

      {/* CSS for animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-20px);
            }
          }

          @keyframes float-slow {
            0%, 100% {
              transform: translateY(0px) translateX(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-30px) translateX(-15px) rotate(5deg);
            }
          }

          @keyframes float-delayed {
            0%, 100% {
              transform: translateY(0px) translateX(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-25px) translateX(20px) rotate(-5deg);
            }
          }

          .animate-float {
            animation: float 8s ease-in-out infinite;
          }

          .animate-float-slow {
            animation: float-slow 10s ease-in-out infinite;
          }

          .animate-float-delayed {
            animation: float-delayed 9s ease-in-out infinite;
          }

          @keyframes gradient-shift {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }

          @keyframes gradient-text {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }

          .animate-gradient-shift {
            background-size: 200% 200%;
            animation: gradient-shift 8s ease infinite;
          }

          .animate-gradient-text {
            background-size: 200% 200%;
            animation: gradient-text 5s ease infinite;
          }

          .gradient-text {
            background: linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary) / 0.8));
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }
        `
      }} />
    </div>
  );
}
